import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import { Modal } from '../Modal';
import { FormInput } from '../ui/FormInput';
import { FormTextArea } from '../ui/FormTextArea';
import { FormSelect } from '../ui/FormSelect';
import { FileInput } from '../Listings/Forms/FormComponents/FileInput';
import { supabase } from '../../lib/supabase';
import { useAdmin } from '../../lib/hooks/useAdmin';
import type { QuizFormQuestion, QuizFormOption } from '../../types/quizzes';

const CATEGORY_KEYS: Record<string, string> = {
  wellness: 'wellnessHealth',
  meditation: 'meditation',
  yoga: 'yoga',
  nutrition: 'nutrition',
  fitness: 'fitness',
  mindfulness: 'mindfulness',
  personal_growth: 'personalGrowth',
  parenting: 'parenting'
};

const LANGUAGE_KEYS: Record<string, string> = {
  en: 'english',
  es: 'spanish',
  fr: 'french',
  de: 'german',
  it: 'italian'
};

const categories = [
  { value: 'wellness' },
  { value: 'meditation' },
  { value: 'yoga' },
  { value: 'nutrition' },
  { value: 'fitness' },
  { value: 'mindfulness' },
  { value: 'personal_growth' },
  { value: 'parenting' }
];

const languages = [
  { value: 'en' },
  { value: 'es' },
  { value: 'fr' },
  { value: 'de' },
  { value: 'it' }
];

interface CourseFormProps {
  onClose: () => void;
  onSuccess: () => void;
}

interface QuizData {
  title: string;
  description: string;
  questions: QuizFormQuestion[];
}

interface LessonData {
  title: string;
  description: string;
  duration: string;
  type: 'video' | 'pdf' | 'text' | 'quiz';
  content?: string;
  video_url?: string;
  quiz?: QuizData;
}

interface FormData {
  title: string;
  description: string;
  category: string;
  language: string;
  lessons: LessonData[];
}

function CourseForm({ onClose, onSuccess }: CourseFormProps) {
  const { t } = useTranslation();
  const { isAdmin } = useAdmin();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<FormData>({
    title: '',
    description: '',
    category: '',
    language: '',
    lessons: []
  });
  const [thumbnail, setThumbnail] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [expandedQuiz, setExpandedQuiz] = useState<number | null>(null);

  const addLesson = () => {
    setFormData(prev => ({
      ...prev,
      lessons: [
        ...prev.lessons,
        {
          title: '',
          description: '',
          duration: '',
          type: 'video',
          content: ''
        }
      ]
    }));
  };

  const removeLesson = (index: number) => {
    setFormData(prev => ({
      ...prev,
      lessons: prev.lessons.filter((_, i) => i !== index)
    }));
    if (expandedQuiz === index) {
      setExpandedQuiz(null);
    }
  };

  const updateLesson = (index: number, field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      lessons: prev.lessons.map((lesson, i) => {
        if (i !== index) return lesson;

        // If changing to quiz type, initialize quiz data
        if (field === 'type' && value === 'quiz' && !lesson.quiz) {
          return {
            ...lesson,
            [field]: value,
            quiz: {
              title: lesson.title || 'Quiz',
              description: '',
              questions: []
            }
          };
        }

        return { ...lesson, [field]: value };
      })
    }));
  };

  const updateQuiz = (lessonIndex: number, field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      lessons: prev.lessons.map((lesson, i) => {
        if (i !== lessonIndex || !lesson.quiz) return lesson;
        return {
          ...lesson,
          quiz: {
            ...lesson.quiz,
            [field]: value
          }
        };
      })
    }));
  };

  const addQuestion = (lessonIndex: number) => {
    setFormData(prev => ({
      ...prev,
      lessons: prev.lessons.map((lesson, i) => {
        if (i !== lessonIndex || !lesson.quiz) return lesson;
        return {
          ...lesson,
          quiz: {
            ...lesson.quiz,
            questions: [
              ...lesson.quiz.questions,
              {
                question_text: '',
                explanation: '',
                options: [
                  { option_text: '', is_correct: true },
                  { option_text: '', is_correct: false },
                  { option_text: '', is_correct: false },
                  { option_text: '', is_correct: false }
                ]
              }
            ]
          }
        };
      })
    }));
  };

  const removeQuestion = (lessonIndex: number, questionIndex: number) => {
    setFormData(prev => ({
      ...prev,
      lessons: prev.lessons.map((lesson, i) => {
        if (i !== lessonIndex || !lesson.quiz) return lesson;
        return {
          ...lesson,
          quiz: {
            ...lesson.quiz,
            questions: lesson.quiz.questions.filter((_, qi) => qi !== questionIndex)
          }
        };
      })
    }));
  };

  const updateQuestion = (lessonIndex: number, questionIndex: number, field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      lessons: prev.lessons.map((lesson, i) => {
        if (i !== lessonIndex || !lesson.quiz) return lesson;
        return {
          ...lesson,
          quiz: {
            ...lesson.quiz,
            questions: lesson.quiz.questions.map((q, qi) =>
              qi === questionIndex ? { ...q, [field]: value } : q
            )
          }
        };
      })
    }));
  };

  const updateOption = (lessonIndex: number, questionIndex: number, optionIndex: number, field: string, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      lessons: prev.lessons.map((lesson, i) => {
        if (i !== lessonIndex || !lesson.quiz) return lesson;
        return {
          ...lesson,
          quiz: {
            ...lesson.quiz,
            questions: lesson.quiz.questions.map((q, qi) => {
              if (qi !== questionIndex) return q;
              return {
                ...q,
                options: q.options.map((opt, oi) => {
                  if (field === 'is_correct') {
                    // Only one option can be correct
                    return { ...opt, is_correct: oi === optionIndex };
                  }
                  return oi === optionIndex ? { ...opt, [field]: value } : opt;
                })
              };
            })
          }
        };
      })
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (currentStep === 1) {
      setCurrentStep(2);
      return;
    }

    if (formData.lessons.length === 0) {
      setError(t('forms.course.addAtLeastOneLesson'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Upload thumbnail if provided
      let thumbnailUrl = '';
      if (thumbnail) {
        const { data: imageData, error: imageError } = await supabase.storage
          .from('course-materials')
          .upload(`${user.id}/thumbnails/${Date.now()}-${thumbnail.name}`, thumbnail);
        
        if (imageError) throw imageError;
        
        const { data: { publicUrl } } = supabase.storage
          .from('course-materials')
          .getPublicUrl(imageData.path);
        
        thumbnailUrl = publicUrl;
      }

      // Create course
      const { data: course, error: courseError } = await supabase
        .from('courses')
        .insert([{
          user_id: user.id,
          title: formData.title,
          description: formData.description,
          category: formData.category,
          language: formData.language,
          price: 0,
          currency: 'EUR',
          thumbnail_url: thumbnailUrl
        }])
        .select()
        .single();

      if (courseError) throw courseError;

      // Upload lesson files and create lessons
      for (let index = 0; index < formData.lessons.length; index++) {
        const lesson = formData.lessons[index];
        let content_url = '';
        let quiz_id = null;

        if (lesson.type === 'text') {
          content_url = lesson.content || '';
        } else if (lesson.type === 'video' && lesson.video_url) {
          // Convert watch URL to embed URL if needed
          const videoId = lesson.video_url.match(/(?:\/embed\/|watch\?v=|\/v\/|youtu\.be\/|\/v=|\/e\/|watch\?v%3D|watch\?feature=player_embedded&v=|%2Fvideos%2F|embed%\u200C\u200B2F|youtu.be%2F|v%2F)([^#\&\?\n]*)/);
          content_url = videoId ? `https://www.youtube.com/embed/${videoId[1]}` : lesson.video_url;
        } else if (lesson.type === 'quiz' && lesson.quiz) {
          // Create the quiz first
          const { data: quizData, error: quizError } = await supabase
            .from('quizzes')
            .insert({
              title: lesson.quiz.title || lesson.title,
              description: lesson.quiz.description,
              passing_score: 70
            })
            .select()
            .single();

          if (quizError) throw quizError;
          quiz_id = quizData.id;

          // Create questions
          for (let qIndex = 0; qIndex < lesson.quiz.questions.length; qIndex++) {
            const question = lesson.quiz.questions[qIndex];

            const { data: questionData, error: questionError } = await supabase
              .from('quiz_questions')
              .insert({
                quiz_id: quiz_id,
                question_text: question.question_text,
                explanation: question.explanation,
                order_number: qIndex + 1
              })
              .select()
              .single();

            if (questionError) throw questionError;

            // Create options
            const optionsToInsert = question.options.map((opt, oIndex) => ({
              question_id: questionData.id,
              option_text: opt.option_text,
              is_correct: opt.is_correct,
              order_number: oIndex + 1
            }));

            const { error: optionsError } = await supabase
              .from('quiz_options')
              .insert(optionsToInsert);

            if (optionsError) throw optionsError;
          }
        }

        // Create the lesson
        const { error: lessonError } = await supabase
          .from('lessons')
          .insert({
            course_id: course.id,
            title: lesson.title,
            description: lesson.description,
            content_url,
            duration: parseInt(lesson.duration) || 0,
            type: lesson.type,
            order_number: index + 1,
            quiz_id
          });

        if (lessonError) throw lessonError;
      }
      
      onSuccess();
    } catch (err: any) {
      console.error('Error creating course:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal title={t('forms.course.createCourse')} onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-6">
        {currentStep === 1 ? (
          <div className="space-y-6">
            <FormInput
              label={t('forms.course.courseTitle')}
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              required
            />

            <FormTextArea
              label={t('forms.course.description')}
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              required
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormSelect
                label={t('forms.course.category')}
                value={formData.category}
                onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                options={categories.map(c => ({ value: c.value, label: t(`forms.course.${CATEGORY_KEYS[c.value]}`) }))}
                required
              />

              <FormSelect
                label={t('forms.course.language')}
                value={formData.language}
                onChange={(e) => setFormData(prev => ({ ...prev, language: e.target.value }))}
                options={languages.map(l => ({ value: l.value, label: t(`forms.course.${LANGUAGE_KEYS[l.value]}`) }))}
                required
              />
            </div>

            <FileInput
              label={t('forms.course.thumbnail')}
              onChange={(files) => setThumbnail(files[0])}
              maxFiles={1}
              maxSize={2}
              accept="image/*"
              description={t('forms.course.thumbnailDesc')}
            />
          </div>
        ) : (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-medium text-content">{t('forms.course.courseLessons')}</h3>
              <button
                type="button"
                onClick={addLesson}
                className="text-sm text-accent-text hover:text-accent-text/80"
              >
                {t('forms.course.addLesson')}
              </button>
            </div>

            {formData.lessons.map((lesson, index) => (
              <div key={index} className="space-y-4 p-4 bg-accent-base/5 rounded-lg">
                <div className="flex justify-between">
                  <h4 className="font-medium text-content">{t('forms.course.lessonNumber', { index: index + 1 })}</h4>
                  <button
                    type="button"
                    onClick={() => removeLesson(index)}
                    className="text-red-600 hover:text-red-700"
                  >
                    {t('forms.common.remove')}
                  </button>
                </div>

                <FormInput
                  label={t('forms.course.title')}
                  value={lesson.title}
                  onChange={(e) => updateLesson(index, 'title', e.target.value)}
                  required
                />

                <FormTextArea
                  label={t('forms.course.lessonDescription')}
                  value={lesson.description}
                  onChange={(e) => updateLesson(index, 'description', e.target.value)}
                  required
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormSelect
                    label={t('forms.course.contentType')}
                    value={lesson.type}
                    onChange={(e) => {
                      const type = e.target.value as 'video' | 'pdf' | 'text' | 'quiz';
                      updateLesson(index, 'type', type);
                      // Clear content when switching types
                      if (type !== 'quiz') {
                        updateLesson(index, 'content', '');
                        updateLesson(index, 'video_url', '');
                      }
                    }}
                    options={[
                      { value: 'video', label: t('forms.course.video') },
                      { value: 'text', label: t('forms.course.textContent') },
                      { value: 'quiz', label: t('forms.course.quiz') }
                    ]}
                    required
                  />

                  <FormInput
                    label={t('forms.course.duration')}
                    type="number"
                    min="1"
                    value={lesson.duration}
                    onChange={(e) => updateLesson(index, 'duration', e.target.value)}
                    required={lesson.type !== 'quiz'}
                  />
                </div>

                {lesson.type === 'text' ? (
                  <FormTextArea
                    label={t('forms.course.content')}
                    value={lesson.content}
                    onChange={(e) => updateLesson(index, 'content', e.target.value)}
                    required
                  />
                ) : lesson.type === 'video' ? (
                  <div>
                    <label className="block text-sm font-medium text-content mb-2">{t('forms.course.videoUrl')}</label>
                    <input
                      type="text"
                      value={lesson.video_url || ''}
                      onChange={(e) => updateLesson(index, 'video_url', e.target.value)}
                      placeholder={t('forms.course.videoUrlPlaceholder')}
                      className="w-full px-4 py-2 border border-accent-text/20 rounded-lg focus:border-accent-text focus:ring-1 focus:ring-accent-text/20"
                      required
                    />
                    <p className="mt-1 text-sm text-content/60">
                      {t('forms.course.videoUrlHelper')}
                    </p>
                  </div>
                ) : lesson.type === 'quiz' && lesson.quiz ? (
                  <div className="space-y-4 border border-accent-text/20 rounded-lg p-4">
                    <div className="flex justify-between items-center">
                      <h5 className="font-medium text-content">{t('forms.course.quizQuestions')}</h5>
                      <button
                        type="button"
                        onClick={() => setExpandedQuiz(expandedQuiz === index ? null : index)}
                        className="text-sm text-accent-text hover:text-accent-text/80 flex items-center"
                      >
                        {expandedQuiz === index ? (
                          <>
                            <ChevronUp className="h-4 w-4 mr-1" />
                            {t('forms.course.collapse')}
                          </>
                        ) : (
                          <>
                            <ChevronDown className="h-4 w-4 mr-1" />
                            {t('forms.course.expand', { count: lesson.quiz.questions.length })}
                          </>
                        )}
                      </button>
                    </div>

                    {expandedQuiz === index && (
                      <div className="space-y-6">
                        <FormInput
                          label={t('forms.course.quizTitle')}
                          value={lesson.quiz.title}
                          onChange={(e) => updateQuiz(index, 'title', e.target.value)}
                          required
                        />

                        <FormTextArea
                          label={t('forms.course.quizDescription')}
                          value={lesson.quiz.description}
                          onChange={(e) => updateQuiz(index, 'description', e.target.value)}
                        />

                        {lesson.quiz.questions.map((question, qIndex) => (
                          <div key={qIndex} className="bg-gray-50 rounded-lg p-4 space-y-4">
                            <div className="flex justify-between items-start">
                              <span className="text-sm font-medium text-content/60">{t('forms.course.questionNumber', { index: qIndex + 1 })}</span>
                              <button
                                type="button"
                                onClick={() => removeQuestion(index, qIndex)}
                                className="text-red-600 hover:text-red-700"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>

                            <FormTextArea
                              label={t('forms.course.question')}
                              value={question.question_text}
                              onChange={(e) => updateQuestion(index, qIndex, 'question_text', e.target.value)}
                              required
                            />

                            <div className="space-y-2">
                              <label className="block text-sm font-medium text-content">{t('forms.course.options')}</label>
                              {question.options.map((option, oIndex) => (
                                <div key={oIndex} className="flex items-center gap-2">
                                  <input
                                    type="radio"
                                    name={`correct-${index}-${qIndex}`}
                                    checked={option.is_correct}
                                    onChange={() => updateOption(index, qIndex, oIndex, 'is_correct', true)}
                                    className="w-4 h-4 text-accent-text"
                                  />
                                  <input
                                    type="text"
                                    value={option.option_text}
                                    onChange={(e) => updateOption(index, qIndex, oIndex, 'option_text', e.target.value)}
                                    placeholder={t('forms.course.optionPlaceholder', { index: oIndex + 1 })}
                                    className="flex-1 px-3 py-2 border border-accent-text/20 rounded-lg focus:border-accent-text focus:ring-1 focus:ring-accent-text/20"
                                    required
                                  />
                                </div>
                              ))}
                            </div>

                            <FormTextArea
                              label={t('forms.course.explanation')}
                              value={question.explanation}
                              onChange={(e) => updateQuestion(index, qIndex, 'explanation', e.target.value)}
                              required
                            />
                          </div>
                        ))}

                        <button
                          type="button"
                          onClick={() => addQuestion(index)}
                          className="w-full py-2 border-2 border-dashed border-accent-text/30 rounded-lg text-accent-text hover:border-accent-text hover:bg-accent-base/5 flex items-center justify-center"
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          {t('forms.course.addQuestion')}
                        </button>
                      </div>
                    )}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        )}

        {error && (
          <div className="text-red-600 text-sm">{error}</div>
        )}

        <div className="flex justify-end space-x-3">
          {currentStep === 2 && (
            <button
              type="button"
              onClick={() => setCurrentStep(1)}
              className="px-4 py-2 text-sm font-medium text-content hover:text-content/80"
            >
              {t('forms.common.back')}
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-content hover:text-content/80"
          >
            {t('forms.common.cancel')}
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-white bg-accent-text rounded-md hover:bg-accent-text/90 disabled:opacity-50"
          >
            {loading ? t('forms.common.creating') : currentStep === 1 ? t('forms.common.next') : t('forms.course.createCourse')}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export { CourseForm };